import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

/**
 * Where a chat message came from: which campaign step ran it, and who sent it (PREC-3908).
 *
 * The Outbox shows a lead's messages with no indication of which step of the sequence produced
 * each one, and every sent message claimed to be from "You" regardless of which colleague's
 * mailbox actually sent it — misleading precisely in the case that matters, where one lead is
 * contacted by two Signalist users.
 */

/** The manual-send step id: a message typed by hand, not produced by a campaign step. */
const MANUAL_STEP_ID = "n99";

/** The invite-acceptance gate. A condition, not a step the lead ever receives. */
const INVITE_ACCEPTANCE_GATE_STEP_ID = "n99-cond";

/** The shape this module needs from a campaign node; `level` is what the builder labels it. */
interface CampaignNode {
	id?: string;
	data?: { level?: number };
}

/** A campaign out of `list.campaignConfig`, keyed there by uuid. */
interface CampaignConfigEntry {
	name?: string;
	initialNodes?: CampaignNode[];
}

/**
 * The campaign this conversation belongs to, out of the list's `campaignConfig` JSON.
 *
 * Campaign config has no table of its own — it is a JSON object keyed by campaign uuid, whose
 * literal `name` key is list-level metadata rather than a campaign (apps/api/CLAUDE.md §7.1).
 */
export const campaignOf = (
	campaignConfig: unknown,
	campaignId: string | null | undefined
): CampaignConfigEntry | null => {
	if (!campaignConfig || !campaignId || campaignId === "name") {
		return null;
	}

	const entry = (campaignConfig as Record<string, CampaignConfigEntry>)[campaignId];

	return entry ?? null;
};

/**
 * The step number to show against a message, or null when it did not come from a campaign step.
 *
 * Three ids are deliberately unnumbered, matching how the rest of the app reads them: `n99` is a
 * manual send, a null step id with `scheduled` set is a message scheduled from the inbox, and
 * `n99-cond` is the invite gate — a condition the lead never receives. The number itself is the
 * node's `level`, the same value the campaign builder prints on the node, so the chat and the
 * builder cannot disagree.
 */
export const conversationStepLabel = (
	conversation: Pick<Conversation, "stepId" | "campaignId" | "scheduled">,
	campaignConfig: unknown
): string | null => {
	const { stepId } = conversation;

	if (!stepId || stepId === MANUAL_STEP_ID || stepId === INVITE_ACCEPTANCE_GATE_STEP_ID) {
		return null;
	}

	const level = campaignOf(campaignConfig, conversation.campaignId)?.initialNodes?.find(
		(node) => node.id === stepId
	)?.data?.level;

	return typeof level === "number" ? `Step ${level}` : null;
};

/** Everything needed to link to the campaign builder, or null when the campaign is unknown. */
export interface CampaignLinkTarget {
	listId: number;
	campaignId: string;
	name: string;
}

/**
 * The campaign-builder link for a message's campaign.
 *
 * Needs the list the campaign lives on, which the conversation carries only sometimes — the
 * caller passes the lead's list as the fallback, since a lead's messages belong to its own list
 * unless the row says otherwise.
 */
export const conversationCampaignLink = (
	conversation: Pick<Conversation, "campaignId" | "listId" | "stepId">,
	campaignConfig: unknown,
	fallbackListId: number | null | undefined
): CampaignLinkTarget | null => {
	// A manual send is not part of the sequence even when the row carries the lead's campaign:
	// the webhook that records a mail written in the user's own client copies `campaignId` from
	// the lead (PREC-4170), and a "View the campaign" link under it reads as the campaign having
	// mailed the lead — the very misattribution that ticket removed from the step label.
	if (conversation.stepId === MANUAL_STEP_ID) {
		return null;
	}

	const campaign = campaignOf(campaignConfig, conversation.campaignId);
	const listId = conversation.listId ?? fallbackListId;

	if (!campaign?.name || !conversation.campaignId || typeof listId !== "number") {
		return null;
	}

	return { listId, campaignId: conversation.campaignId, name: campaign.name };
};

/**
 * How to name the account a message was sent from.
 *
 * "You" is only honest when the signed-in user is the sender. When a colleague's mailbox sent it,
 * their name is the useful answer — that is the edge case the ticket names, one lead contacted
 * from two Signalist users. Falls back to the sender's own name when we cannot tell.
 */
export const sentBySuffix = (
	conversation: Pick<Conversation, "user">,
	currentUserId: number | null | undefined
): string | null => {
	const senderId = conversation.user?.id;

	if (typeof senderId !== "number" || typeof currentUserId !== "number") {
		return null;
	}

	return senderId === currentUserId ? "You" : null;
};
